import { useRef } from 'react';

type Props = {
  value: string;            // 0-6 digits
  onChange: (v: string) => void;
  error?: boolean;
};

export function OtpInput({ value, onChange, error }: Props) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const cells = Array.from({ length: 6 }, (_, i) => value[i] || '');

  const set = (i: number, v: string) => {
    const digits = v.replace(/\D/g, '');
    if (!digits) {
      onChange(value.slice(0, i) + value.slice(i + 1));
      return;
    }
    if (digits.length > 1) {
      // Paste always starts from cell 0, regardless of which cell the user
      // pasted into. The expectation is "pasted OTP fills the field" — if
      // we anchored at `i`, a mid-field paste would truncate the suffix.
      const merged = digits.slice(0, 6);
      onChange(merged);
      const focusIdx = Math.min(merged.length, 5);
      refs.current[focusIdx]?.focus();
      return;
    }
    const arr = cells.slice();
    arr[i] = digits;
    onChange(arr.join(''));
    if (i < 5) refs.current[i + 1]?.focus();
  };

  const onKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !cells[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  return (
    <div className="grid grid-cols-6 gap-2">
      {cells.map((c, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          value={c}
          inputMode="numeric"
          autoComplete="one-time-code"
          // maxLength={6} (NOT 1) is intentional — lets a paste deliver
          // the full OTP in one onChange event so we can fill all cells.
          maxLength={6}
          onChange={(e) => set(i, e.target.value)}
          onKeyDown={(e) => onKey(i, e)}
          onFocus={(e) => e.target.select()}
          className={[
            'text-center font-bold text-xl rounded-[var(--radius-md)] border bg-white outline-none h-14',
            // Explicit dark text: the box is hard-coded bg-white (theme-independent),
            // so the digit colour must be too — inheriting var(--color-text) made the
            // digits near-white (#e8eaed) and invisible when the device is in dark mode.
            error
              ? 'border-[var(--color-danger)] bg-[var(--status-danger-bg)] text-[var(--color-danger)]'
              : c
              ? 'border-[var(--color-primary-500)] bg-[var(--status-success-bg)] text-[#1E1E1E]'
              : 'border-[var(--color-border)] text-[#1E1E1E]',
          ].join(' ')}
        />
      ))}
    </div>
  );
}
